/**
 * ToothAnatomyVisual — educational 5-zone SVG visualizer of the oral ecosystem.
 *
 * 3D digital-twin effect via subtle CSS transform on the SVG: gentle perspective
 * tilt + slow rotation on hover. Clickable zones remain accurate to pointer.
 */
import React, { useState } from "react";

const BAND_FILL = {
  green:  "#22C55E",
  yellow: "#EAB308",
  orange: "#F97316",
  red:    "#EF4444",
};

const ZONE_LABELS = {
  bg: {
    enamel:       "Емайл",
    dentin:       "Дентин",
    periodontium: "Венци",
    microbiome:   "Микробиом",
    saliva:       "Слюнка",
  },
  en: {
    enamel:       "Enamel",
    dentin:       "Dentin",
    periodontium: "Gums",
    microbiome:   "Microbiome",
    saliva:       "Saliva",
  },
  es: {
    enamel:       "Esmalte",
    dentin:       "Dentina",
    periodontium: "Encías",
    microbiome:   "Microbioma",
    saliva:       "Saliva",
  },
};

function bandColor(band) {
  return BAND_FILL[band] || "#94A3B8";
}

/* Each clickable zone is a plain <g> wrapper around its visible shape.
   Painting order matters: later <g>s sit on top and capture clicks first.
   Saliva is the outermost back-most layer. Enamel/Dentin are top-front. */

export default function ToothAnatomyVisual({ ecosystem, onSelect, selected, lang = "bg" }) {
  const lbl = ZONE_LABELS[lang] || ZONE_LABELS.bg;
  const enamel = ecosystem?.enamel || {};
  const dentin = ecosystem?.dentin || {};
  const perio = ecosystem?.periodontium || {};
  const micro = ecosystem?.microbiome || {};
  const saliva = ecosystem?.saliva || {};

  const handle = (zone) => (e) => {
    e.stopPropagation();
    onSelect && onSelect(zone);
  };

  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="relative w-full"
      data-testid="tooth-anatomy-visual"
      style={{ perspective: "1000px" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <svg
        viewBox="0 0 600 540"
        className="w-full h-auto transition-transform duration-700 ease-out"
        style={{
          maxHeight: 540,
          transform: hovered
            ? "rotateY(-10deg) rotateX(4deg) scale(1.02)"
            : "rotateY(0deg) rotateX(0deg) scale(1)",
          transformStyle: "preserve-3d",
          filter: hovered ? "drop-shadow(0 20px 35px rgba(0,128,160,0.18))" : "drop-shadow(0 4px 12px rgba(0,0,0,0.05))",
        }}
        role="img"
        aria-label="Tooth anatomical visualization with 3D twin effect"
      >
        <defs>
          <radialGradient id="enamelGlow" cx="50%" cy="35%" r="60%">
            <stop offset="0%" stopColor="#FAFAFA" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#E2E8F0" stopOpacity="0.3" />
          </radialGradient>
          <radialGradient id="dentinGlow" cx="50%" cy="50%" r="55%">
            <stop offset="0%" stopColor="#FBCFB4" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.2" />
          </radialGradient>
          <radialGradient id="salivaGlow" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#7DD3FC" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#0EA5E9" stopOpacity="0.05" />
          </radialGradient>
        </defs>

        {/* Saliva environment — outermost glow ring */}
        <g onClick={handle("saliva")} style={{ cursor: "pointer" }} data-testid="zone-saliva">
          <ellipse
            cx="300" cy="270" rx="280" ry="240"
            fill="url(#salivaGlow)"
            stroke={bandColor(saliva.band)}
            strokeOpacity="0.55"
            strokeWidth={selected === "saliva" ? 6 : 3}
            strokeDasharray="8 4"
          >
            <animate attributeName="stroke-dashoffset" from="0" to="24" dur="6s" repeatCount="indefinite" />
          </ellipse>
        </g>

        {/* Microbiome — speckled background pattern around tooth.
            Bacterial dots are scattered AROUND the tooth so clicks are reachable. */}
        <g onClick={handle("microbiome")} style={{ cursor: "pointer" }} data-testid="zone-microbiome">
          {/* Invisible large hit area covering microbiome dots region */}
          <rect x="40" y="60" width="520" height="380" fill="rgba(0,0,0,0)" />
          {[
            [110, 200], [150, 110], [490, 130], [510, 240],
            [120, 380], [490, 400], [200, 80], [400, 80],
            [80, 290], [520, 320],
          ].map(([cx, cy], i) => (
            <circle
              key={`m-${i}`}
              cx={cx} cy={cy} r={selected === "microbiome" ? 11 : 7}
              fill={bandColor(micro.band)}
              opacity="0.7"
              stroke={selected === "microbiome" ? bandColor(micro.band) : "none"}
              strokeWidth={selected === "microbiome" ? 2 : 0}
            />
          ))}
        </g>

        {/* Gum / periodontium — bottom base */}
        <g onClick={handle("periodontium")} style={{ cursor: "pointer" }} data-testid="zone-periodontium">
          <path
            d="M 90,420 Q 300,510 510,420 L 510,495 L 90,495 Z"
            fill={bandColor(perio.band)}
            fillOpacity="0.4"
            stroke={bandColor(perio.band)}
            strokeWidth={selected === "periodontium" ? 6 : 3}
          />
        </g>

        {/* Tooth body — Enamel (outer) */}
        <g onClick={handle("enamel")} style={{ cursor: "pointer" }} data-testid="zone-enamel">
          <path
            d="M 240,110 Q 300,70 360,110 Q 410,200 410,330 Q 410,420 380,440 L 220,440 Q 190,420 190,330 Q 190,200 240,110 Z"
            fill="url(#enamelGlow)"
            stroke={bandColor(enamel.band)}
            strokeWidth={selected === "enamel" ? 8 : 4}
          />
        </g>

        {/* Dentin — inner tooth (TOP layer so it can be selected) */}
        <g onClick={handle("dentin")} style={{ cursor: "pointer" }} data-testid="zone-dentin">
          <path
            d="M 265,170 Q 300,135 335,170 Q 375,240 375,330 Q 375,400 360,415 L 240,415 Q 225,400 225,330 Q 225,240 265,170 Z"
            fill="url(#dentinGlow)"
            stroke={bandColor(dentin.band)}
            strokeWidth={selected === "dentin" ? 7 : 3}
          />
          {/* Pulp chamber (illustrative) */}
          <ellipse cx="300" cy="290" rx="22" ry="58" fill="#EF4444" opacity="0.18" />
        </g>

        {/* Labels — band-coloured, non-interactive */}
        <g style={{ pointerEvents: "none" }} fontSize="13" fontWeight="600" fontFamily="system-ui">
          <text x="300" y="40" textAnchor="middle" fill={bandColor(enamel.band)}>
            {lbl.enamel} · {enamel.score ?? "—"}
          </text>
          <text x="300" y="525" textAnchor="middle" fill={bandColor(perio.band)}>
            {lbl.periodontium} · {perio.score ?? "—"}
          </text>
          <text x="60" y="200" textAnchor="start" fill={bandColor(micro.band)}>
            {lbl.microbiome} · {micro.score ?? "—"}
          </text>
          <text x="540" y="200" textAnchor="end" fill={bandColor(saliva.band)}>
            {lbl.saliva} · {saliva.score ?? "—"}
          </text>
          <text x="300" y="380" textAnchor="middle" fill={bandColor(dentin.band)} fontSize="12">
            {lbl.dentin} · {dentin.score ?? "—"}
          </text>
        </g>
      </svg>
    </div>
  );
}
